import { useRef, useCallback } from 'react';

/**
 * One idempotency key (and its payment intent) per payment ATTEMPT.
 *
 * - Reused across an *ambiguous* retry (network / 5xx — the charge MIGHT have
 *   landed): same key + same PI ⇒ Chargebee replays the original result instead
 *   of charging again.
 * - Reset on a *definitive* failure (4xx decline/validation — the charge provably
 *   did NOT land) or on success, so the next attempt starts clean and can retry
 *   a new card immediately (no 30-minute replay lockout).
 */
export function usePaymentAttempt() {
  const keyRef = useRef<string | null>(null);
  const piRef = useRef<string | undefined>(undefined);

  const mint = (): string => {
    try {
      if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) {
        return (crypto as any).randomUUID();
      }
    } catch { /* fall through */ }
    return 'idem-' + Date.now() + '-' + Math.random().toString(36).slice(2);
  };

  // Active key, minted on first use of an attempt.
  const beginKey = useCallback((): string => {
    if (!keyRef.current) keyRef.current = mint();
    return keyRef.current;
  }, []);

  // PI carried over from a prior ambiguous attempt (undefined ⇒ create a fresh one).
  const getPI = useCallback((): string | undefined => piRef.current, []);
  const setPI = useCallback((pi: string | undefined): void => { piRef.current = pi; }, []);

  // Charge confirmed ⇒ next attempt is brand new.
  const succeed = useCallback((): void => { keyRef.current = null; piRef.current = undefined; }, []);

  // Decide post-error: rotate on a definitive 4xx, keep on ambiguous (network/5xx).
  const fail = useCallback((err: any): void => {
    const status: number | undefined = err?.status ?? err?.response?.status;
    const definitive =
      typeof status === 'number' && status >= 400 && status < 500 && status !== 408 && status !== 429;
    if (definitive) { keyRef.current = null; piRef.current = undefined; }
    // else: keep key + PI so a retry replays idempotently
  }, []);

  return { beginKey, getPI, setPI, succeed, fail };
}
